import React from "react";
import PropTypes from "prop-types";

import useCssVarHeight from "../useCssVarHeight";

/**
 * Row 0 — an ongoing SparkTG call, pinned above every screen's row 1 (Step 88).
 *
 * The desktop pill (SparkTGCallFloater) is fixed at the bottom centre, which on
 * a phone is where the pinned Create Rx / Call patient bar sits. Here the same
 * call is a green row at the top of the page instead.
 *
 * DRAWS, DECIDES NOTHING. SparkTGCallFloater still owns everything: whether a
 * call is showing at all, standing aside for a LiveKit call, the name fallback
 * and where a tap goes. It hands this row the result.
 *
 * FIXED, NOT STICKY (Step 89). The row is `position: fixed` at the top, so it
 * stays on screen however far the page scrolls; the rows under it scroll away
 * as they always have. Sticky would pin to <body>, which is a scroll container
 * that never scrolls (mobileViewWorking.md section 6), so a sticky row
 * scrolled off with everything else.
 *
 * The outer div is an IN-FLOW PLACEHOLDER. It renders before <Switch>, so it is
 * the first thing in the page, and its height is the fixed row's measured
 * height (--zym-call-h). So the screen starts below the row instead of under
 * it. Every pinned row and sheet offset adds the same property (mobile.scss).
 * It is cleared on unmount, because a call ending mid-screen would otherwise
 * leave a 40px gap where the row was.
 */

const ChevronIcon = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path
      d="M4 2l4 4-4 4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobileCallBar = ({ patientName, appointmentId, icon, onOpen }) => {
  /* Measures the fixed row itself, so the placeholder holds its real height. */
  const ref = useCssVarHeight("--zym-call-h", [], { clear: true });

  return (
    <div className="zym-callbar">
      {/* A real button: Enter and Space open it natively, which is what the
          pill's role="button" + onKeyDown pair does by hand. */}
      <button
        ref={ref}
        type="button"
        className="zym-callbar-hit"
        onClick={onOpen}
        aria-label={`Ongoing call with ${patientName}, open appointment ${appointmentId}`}
        title="Open appointment"
      >
        <span className="zym-callbar-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="zym-callbar-dot" aria-hidden="true" />
        <span className="zym-callbar-name">{patientName}</span>
        <span className="zym-callbar-id">APPT ID {appointmentId}</span>
        <span className="zym-callbar-chev" aria-hidden="true">
          <ChevronIcon />
        </span>
      </button>
    </div>
  );
};

MobileCallBar.propTypes = {
  /** Already falls back to "Ongoing call" in the floater. */
  patientName: PropTypes.string.isRequired,
  appointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  /** The floater's own phone glyph, so both indicators draw the same one. */
  icon: PropTypes.node,
  /** The floater's goToAppointment. */
  onOpen: PropTypes.func.isRequired,
};

export default MobileCallBar;
